import { useCallback, useSyncExternalStore } from "react";

import { getUnixTs } from "@bridgesplit/utils";

export const LOAN_TICK = 1;
export const PORTFOLIO_TICK = 10;
export const REWARDS_TICK = 60;

type Cadence = typeof LOAN_TICK | typeof PORTFOLIO_TICK | typeof REWARDS_TICK;

interface ClockBucket {
    now: number;
    listeners: Set<() => void>;
    timer: ReturnType<typeof setInterval> | null;
}

const buckets = new Map<number, ClockBucket>();

function getBucket(seconds: number): ClockBucket {
    let bucket = buckets.get(seconds);
    if (!bucket) {
        bucket = { now: getUnixTs(), listeners: new Set(), timer: null };
        buckets.set(seconds, bucket);
    }
    return bucket;
}

function subscribe(seconds: number, listener: () => void) {
    const bucket = getBucket(seconds);
    bucket.listeners.add(listener);
    if (bucket.timer === null) {
        // The bucket lives on after all listeners unsubscribe, so its `now` can be stale (up to a
        // full cadence) by the time a fresh subscriber arrives. Refresh before restarting the timer.
        bucket.now = getUnixTs();
        bucket.timer = setInterval(() => {
            bucket.now = getUnixTs();
            bucket.listeners.forEach((l) => l());
        }, seconds * 1000);
    }
    return () => {
        bucket.listeners.delete(listener);
        if (bucket.listeners.size === 0 && bucket.timer !== null) {
            clearInterval(bucket.timer);
            bucket.timer = null;
        }
    };
}

export function useLiveClock(seconds: Cadence): number {
    const subscribeToCadence = useCallback((listener: () => void) => subscribe(seconds, listener), [seconds]);
    const getSnapshot = useCallback(() => getBucket(seconds).now, [seconds]);
    return useSyncExternalStore(subscribeToCadence, getSnapshot);
}

const MS_PER_SECOND = 1000;

/** `useLiveClock` in milliseconds, so it's directly comparable to `Date.now()` / `Date.getTime()`. */
export function useLiveClockMs(seconds: Cadence): number {
    return useLiveClock(seconds) * MS_PER_SECOND;
}
